import React from 'react';
import { TFunction } from 'react-i18next';
import { IMenuItemType } from '@/ui';
import { ROUTES } from '@/models/router.model';
import { Resource } from '@/modules/Permissions';
import { StyledMenuItemLabel, StyledMenuItemWithBadge, StyledMenuNotificationBadge } from './getMenuItemsConfigStyles';

type CanView = (resource: Resource) => boolean;

const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count));

const getFeedbacksMenuItem = (t: TFunction, newFeedbacksCount?: number): IMenuItemType => ({
  label: (
    <StyledMenuItemWithBadge>
      <StyledMenuItemLabel>{t('feedbacks')}</StyledMenuItemLabel>
      {newFeedbacksCount !== undefined && newFeedbacksCount > 0 && (
        <StyledMenuNotificationBadge>{formatBadgeCount(newFeedbacksCount)}</StyledMenuNotificationBadge>
      )}
    </StyledMenuItemWithBadge>
  ),
  key: ROUTES.FEEDBACKS,
});

const getOrdersMenuItem = (t: TFunction, newOrdersCount?: number): IMenuItemType => ({
  label: (
    <StyledMenuItemWithBadge>
      <StyledMenuItemLabel>{t('orders')}</StyledMenuItemLabel>
      {newOrdersCount !== undefined && newOrdersCount > 0 && (
        <StyledMenuNotificationBadge>{formatBadgeCount(newOrdersCount)}</StyledMenuNotificationBadge>
      )}
    </StyledMenuItemWithBadge>
  ),
  key: ROUTES.ORDERS,
});

/**
 * Builds the sidebar menu from the current admin's permissions.
 * A section is shown when at least one of its resources is viewable; a parent
 * item is dropped when all of its children are hidden.
 */
export const getMenuItemsConfig = (
  t: TFunction,
  canView: CanView,
  newFeedbacksCount?: number,
  newOrdersCount?: number,
): IMenuItemType[] => {
  const visible = (resources: Resource[]): boolean => resources.some(canView);
  const items: IMenuItemType[] = [];

  if (visible([Resource.DASHBOARD])) {
    items.push({
      label: t('dashboard'),
      key: 'dashboard-index',
      children: [
        { label: t('nav_dashboard_main_metrics'), key: ROUTES.DASHBOARD },
        { label: t('nav_cancelled_orders'), key: ROUTES.CANCELLED_ORDERS },
      ],
    });
  }
  if (visible([Resource.PROFILE])) {
    items.push({ label: t('profile'), key: ROUTES.PROFILE });
  }
  if (visible([Resource.REGION, Resource.PICKUP])) {
    items.push({ label: t('regions_and_pickup'), key: ROUTES.REGIONS_AND_PICKUP_INDEX });
  }
  if (visible([Resource.CATEGORY, Resource.FILTER, Resource.POPULAR_CATEGORY])) {
    items.push({ label: t('nav_categories_and_filters'), key: ROUTES.CATEGORIES });
  }
  if (visible([Resource.PRODUCT, Resource.PRODUCT_OF_MONTH, Resource.TOP_PRODUCT])) {
    items.push({ label: t('nav_products'), key: ROUTES.PRODUCTS });
  }
  // Stock balances have a dedicated resource: object managers see them without product
  // access, while the site administrator keeps products but not stock balances.
  if (visible([Resource.STOCK_BALANCE])) {
    items.push({ label: t('nav_stock_balances'), key: ROUTES.STOCK_BALANCES });
  }
  if (visible([Resource.ORDER])) {
    items.push(getOrdersMenuItem(t, newOrdersCount));
  }

  const importChildren = [
    visible([Resource.IMPORT_PRICE]) && { label: t('nav_import_stocks'), key: ROUTES.IMPORT_STOCKS },
    visible([Resource.IMPORT_PRODUCT]) && { label: t('nav_import_products'), key: ROUTES.IMPORT },
    visible([Resource.IMPORT_LIST]) && { label: t('nav_import_list'), key: ROUTES.IMPORTS_LIST },
    visible([Resource.QR_GENERATION]) && { label: t('nav_generate_qr'), key: ROUTES.GENERATE_QR },
  ].filter(Boolean) as IMenuItemType[];
  if (importChildren.length) {
    items.push({ label: t('nav_import'), key: 'import-index', children: importChildren });
  }

  if (visible([Resource.ADMIN_PANEL, Resource.SITE_USER])) {
    items.push({ label: t('users'), key: ROUTES.USERS_LIST_INDEX });
  }
  if (visible([Resource.PARTNER])) {
    items.push({ label: t('partner'), key: ROUTES.PARTNERS_LIST });
  }

  const siteChildren = [
    visible([Resource.BANNER]) && { label: t('banners'), key: ROUTES.BANNERS },
    visible([Resource.CONTENT]) && { label: t('content_pages'), key: ROUTES.CONTENT_PAGES },
    visible([Resource.SUBSCRIPTION]) && { label: t('subscriptions'), key: ROUTES.SUBSCRIPTIONS },
    visible([Resource.SETTING]) && { label: t('settings'), key: ROUTES.SETTINGS },
    visible([Resource.SEO]) && { label: t('seo'), key: ROUTES.SEO },
    visible([Resource.FEED]) && { label: t('feeds'), key: ROUTES.FEEDS },
    visible([Resource.COMMISSION]) && { label: t('commission'), key: ROUTES.COMMISSION },
  ].filter(Boolean) as IMenuItemType[];
  if (siteChildren.length) {
    items.push({ label: t('site_management'), key: '5', children: siteChildren });
  }

  const accountingChildren = [
    visible([Resource.CASH_REGISTER]) && {
      label: t('tab_cash_registers', { ns: 'financialSettings' }),
      key: ROUTES.ACCOUNTING_CASH_REGISTERS_INDEX,
    },
  ].filter(Boolean) as IMenuItemType[];
  if (accountingChildren.length) {
    items.push({ label: t('accounting'), key: 'accounting-index', children: accountingChildren });
  }

  if (visible([Resource.FEEDBACK])) {
    items.push(getFeedbacksMenuItem(t, newFeedbacksCount));
  }

  return items;
};
